import {filter, find} from 'lodash';
import {useCallback, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ActionButton, ConfirmModal, Icon, ToggleFormik, UserInitials} from 'components';
import {UserContext} from 'context';
import {TRANSLATION_KEYS} from 'locale';
import {IpInfo, UserSummary} from 'types';
import {formatDate, getParticipantName} from 'utils/transform';

import {SECURITY_FIELDS} from '../../settings.constants';

import {AddIpModal} from './AddIpModal';
import {IpGrid} from './IpGrid';
import {IpTable} from './IpTable';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SETTINGS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type Props = {
    value: {
        enabled: boolean;
        list: IpInfo[];
    };
    users: UserSummary[];
    className?: string;
    setFieldValue: (field: string, value: any) => void;
}

export const IpWhitelist: React.FC<Props> = ({value, users, className, setFieldValue}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);
    const [addModalProps, setAddModalProps] = useState<{
        open: boolean;
    }>({open: false});
    const [deleteModalProps, setDeleteModalProps] = useState<{
        open: boolean;
        index?: number;
    }>({open: false});

    const handleDelete = useCallback((index: number) => {
        setDeleteModalProps({
            open: true,
            index
        });
    }, []);

    const handleConfirmDelete = useCallback((confirm: boolean, index: number) => {
        if (confirm) {
            const newList = filter(value.list, (_, i) => i !== index);
            setFieldValue(SECURITY_FIELDS.IP_WHITELIST_LIST, newList);
        }
        setDeleteModalProps({open: false});
    }, [value, setFieldValue]);

    const handleAdd = useCallback((confirm: boolean, ipInfo: IpInfo) => {
        if (confirm && ipInfo) {
            const index = value.list?.length || 0;
            setFieldValue(`${SECURITY_FIELDS.IP_WHITELIST_LIST}[${index}]`, ipInfo);
        }
        setAddModalProps({open: false});
    }, [value, setFieldValue]);

    const renderCreatedBy = useCallback((eid: number) => {
        const user = find(users, {eid});
        const userName = getParticipantName(user, t(T_KEYS.IP_WHITELIST.DELETED_USER));
        return (
            <div className='tw-flex tw-items-center'>
                <Icon name='user' className='md:tw-hidden tw-ml-2 tw-mr-4' />
                <UserInitials user={user} className='-md:tw-hidden tw-mr-2' />
                {userName}
            </div>
        );
    }, [users, t]);

    const renderCreatedAt = useCallback((date: Date) => formatDate(date, timeZone, dateFormat), [timeZone, dateFormat]);

    return (
        <div className={className}>
            <div className='tw-ts-h5'>{t(T_KEYS.IP_WHITELIST.TITLE)}</div>
            <div className='tw-flex tw-items-center tw-justify-between'>
                <ToggleFormik
                    className='tw-my-4'
                    name={SECURITY_FIELDS.IP_WHITELIST_ENABLED}
                    label={t(COMMON_T_KEYS.ALL.ENABLED)}
                />
                <ActionButton
                    action='create'
                    onClick={() => setAddModalProps({open: true})}
                >
                    {t(T_KEYS.IP_WHITELIST.ADD_BUTTON)}
                </ActionButton>
            </div>

            <IpTable
                ipInfos={value.list}
                renderCreatedBy={renderCreatedBy}
                renderCreatedAt={renderCreatedAt}
                className='-md:tw-hidden'
                onDelete={handleDelete}
            />

            <IpGrid
                ipInfos={value.list}
                renderCreatedBy={renderCreatedBy}
                renderCreatedAt={renderCreatedAt}
                className='md:tw-hidden'
                onDelete={handleDelete}
            />

            <AddIpModal {...addModalProps} onClose={handleAdd} />
            <ConfirmModal
                open={deleteModalProps.open}
                title={t(T_KEYS.IP_WHITELIST.MODAL.DELETE)}
                onClose={(confirm) => handleConfirmDelete(confirm, deleteModalProps.index)}
            />
        </div>
    );
};